// Where the person chooses what to snapshot: with the camera armed, a drag
// on the slide draws a rectangle, upright on screen, and letting go hands it
// over; a click (no drag to speak of) takes the whole view. Outside the
// rectangle the slide dims so the choice reads at a glance. A hint at the top
// says so, as the scan's bar does for its areas.
//
// Sits over the map and takes its pointer, as the scan's draw layer does
// (scan/DrawAreaLayer.tsx): a drag draws rather than pans. A wheel still
// zooms, handed to the engine; the pinch is the viewport's already.

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from 'react';
import type { ViewportEngine } from '../ViewportEngine';
import type { CaptureRect } from './region';

/** A drag shorter than this either way is a click: the whole view. */
export const MIN_DRAG_PX = 6;

export const CAPTURE_HINT = 'Drag to choose the area to download, or click for the whole view';

export interface Drag {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** The rectangle a drag chose, or the whole `w` × `h` view for a click. */
export function rectOf(drag: Drag, w: number, h: number): CaptureRect {
  const dw = Math.abs(drag.x1 - drag.x0);
  const dh = Math.abs(drag.y1 - drag.y0);
  if (dw < MIN_DRAG_PX || dh < MIN_DRAG_PX) return { x: 0, y: 0, w, h };
  const x = Math.max(0, Math.min(drag.x0, drag.x1));
  const y = Math.max(0, Math.min(drag.y0, drag.y1));
  return { x, y, w: Math.min(w, Math.max(drag.x0, drag.x1)) - x, h: Math.min(h, Math.max(drag.y0, drag.y1)) - y };
}

/** The drag so far: the rest of the view dimmed, a white edge on a dark halo. */
export function drawCapture(
  canvas: HTMLCanvasElement,
  drag: Drag | null,
  dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1,
  size?: { w: number; h: number },
): 'drawn' | 'no-context' {
  const cssW = size?.w ?? canvas.clientWidth;
  const cssH = size?.h ?? canvas.clientHeight;
  const w = Math.max(1, Math.round(cssW * dpr));
  const h = Math.max(1, Math.round(cssH * dpr));
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext('2d');
  if (!ctx) return 'no-context';
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, w, h);
  if (drag) {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const x = Math.min(drag.x0, drag.x1);
    const y = Math.min(drag.y0, drag.y1);
    const dw = Math.abs(drag.x1 - drag.x0);
    const dh = Math.abs(drag.y1 - drag.y0);
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.fillRect(0, 0, cssW, y);
    ctx.fillRect(0, y + dh, cssW, cssH - y - dh);
    ctx.fillRect(0, y, x, dh);
    ctx.fillRect(x + dw, y, cssW - x - dw, dh);
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.strokeRect(x, y, dw, dh);
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = '#fff';
    ctx.strokeRect(x, y, dw, dh);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }
  return 'drawn';
}

export function CaptureLayer({
  engine,
  onCapture,
  onDrawStart,
}: {
  engine: ViewportEngine;
  /** A rectangle was chosen (CSS px of the viewport). */
  onCapture: (rect: CaptureRect) => void;
  /** A drag began: a running scan pauses for it. */
  onDrawStart?: () => void;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const sizeRef = useRef<{ w: number; h: number } | undefined>(undefined);

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!sizeRef.current) sizeRef.current = { w: canvas.clientWidth, h: canvas.clientHeight };
    drawCapture(canvas, dragRef.current, undefined, sizeRef.current);
  };
  const drawRef = useRef(draw);
  drawRef.current = draw;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver((entries) => {
            const box = entries[entries.length - 1]?.contentRect;
            if (box && box.width > 0 && box.height > 0) sizeRef.current = { w: box.width, h: box.height };
            drawRef.current();
          })
        : null;
    ro?.observe(canvas);
    return () => ro?.disconnect();
  }, []);
  useEffect(() => {
    drawRef.current();
  }, [drag]);

  const local = (e: ReactPointerEvent) => {
    const r = hostRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.pointerType !== 'touch') return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const p = local(e);
    dragRef.current = { x0: p.x, y0: p.y, x1: p.x, y1: p.y };
    setDrag(dragRef.current);
    onDrawStart?.();
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const p = local(e);
    dragRef.current = { ...dragRef.current, x1: p.x, y1: p.y };
    setDrag(dragRef.current);
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    const p = local(e);
    const done = { ...d, x1: p.x, y1: p.y };
    dragRef.current = null;
    setDrag(null);
    const host = hostRef.current;
    if (!host) return;
    const rect = rectOf(done, host.clientWidth, host.clientHeight);
    if (rect.w >= 1 && rect.h >= 1) onCapture(rect);
  };
  const onPointerCancel = () => {
    dragRef.current = null;
    setDrag(null);
  };
  const onWheel = (e: ReactWheelEvent<HTMLDivElement>) => {
    if (e.ctrlKey) return;
    engine.scrollZoom(e.nativeEvent, { x: e.clientX, y: e.clientY });
  };

  return (
    <div
      ref={hostRef}
      data-capture-layer
      // Over the map and the scan's layers, under the controls (z 5). A
      // drag here is not analytics (lib/analytics.ts).
      className="ph-no-capture absolute inset-0 z-[4] cursor-crosshair"
      style={{ pointerEvents: 'auto', touchAction: 'none' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onWheel={onWheel}
    >
      <canvas ref={canvasRef} aria-hidden className="pointer-events-none absolute inset-0 h-full w-full" />
      {!drag && (
        <div
          role="status"
          className="pointer-events-none absolute left-1/2 top-3 w-max max-w-[calc(100%-24px)] -translate-x-1/2 rounded-md border border-white/20 bg-black/80 px-3 py-1.5 text-center text-xs leading-snug text-white shadow-lg"
        >
          {CAPTURE_HINT} · <span className="text-white/70">Esc to cancel</span>
        </div>
      )}
    </div>
  );
}
